/** @format */

import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva} from 'class-variance-authority';

import {cn, addTWPrefixToClasses} from '@/lib/utils';

function buttonVariants({variant, size, ghost}) {
    return cva(
        'tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-fs-4 tw-fw-600 tw-transition-colors tw-disabled:pointer-events-none tw-disabled:muted tw-rounded-[9999px]',
        {
            variants: {
                variant: {
                    // Custom outline variants
                    primary: ghost
                        ? 'tw-bg-transparent tw-text-primary tw-border-2 tw-border-primary hover:tw-bg-primary hover:tw-border-primary'
                        : 'tw-bg-primary tw-text-white hover:tw-bg-indigo-900 hover:tw-text-white',
                    secondary: ghost
                        ? 'tw-bg-transparent tw-text-gray-500 tw-border-2 tw-border-gray-500 hover:tw-bg-secondary hover:tw-text-white hover:tw-border-secondary'
                        : 'tw-bg-secondary tw-text-white hover:tw-bg-gray-800',
                    danger: ghost
                        ? 'tw-bg-transparent tw-text-danger tw-border-2 tw-border-danger hover:tw-bg-danger hover:tw-text-white'
                        : 'tw-bg-danger tw-text-white hover:tw-bg-red-700 hover:tw-text-white',
                    warning: ghost
                        ? 'tw-bg-transparent tw-text-yellow-600 tw-border-2 tw-border-yellow-600 hover:tw-bg-warning hover:tw-text-white'
                        : 'tw-bg-warning tw-text-gray-900 hover:tw-bg-yellow-600 hover:tw-text-white',
                    success: ghost
                        ? 'tw-bg-transparent tw-text-success tw-border-2 tw-border-success hover:tw-bg-success hover:tw-text-white'
                        : 'tw-bg-success tw-text-white hover:tw-bg-green-700 hover:tw-text-white',
                    info: ghost
                        ? 'tw-bg-transparent tw-text-blue-500 tw-border-2 tw-border-blue-500 hover:tw-border-info'
                        : 'tw-bg-info tw-text-gray-900 hover:tw-bg-indigo-300 ',
                    disabled:
                        'tw-bg-gray-200 tw-text-gray-500 tw-border-2 tw-border-gray-200 tw-pointer-events-none',
                    incognito: 'tw-bg-transparent  tw-p-0'
                },

                size: {
                    xs: ' tw-p-0',
                    sm: 'tw-py-1 tw-px-3',
                    md: 'tw-py-2 tw-px-4',
                    lg: 'tw-py-3 tw-px-6'
                }
            },
            defaultVariants: {
                variant: 'primary',
                size: 'md'
            }
        }
    )({variant, size});
}

const Button = React.forwardRef(
    ({className, ghost, variant, size, asChild = false, ...props}, ref) => {
        const Comp = asChild ? Slot : 'button';

        let variants = buttonVariants({variant, size, ghost});

        if (variant === 'incognito') {
            variants = variants.replace('tw-inline-flex tw-items-center tw-justify-center', '');
        }

        return (
            <Comp className={cn(variants, addTWPrefixToClasses(className))} ref={ref} {...props} />
        );
    }
);
Button.displayName = 'Button';

export {Button, buttonVariants};
